import { Component, OnInit, OnDestroy, HostListener, HostBinding, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import { ServicoRastreamentoBio } from '../services/servico-rastreamento-bio.service';

interface IndicadorHeatmap {
  top: number;
  left: number;
  width: number;
  height: number;
  cor: string;
  quantidade: number;
  percentual: number;
  borderRadius: number;
  eTopClique: boolean;
}

interface LinkBioPagina {
  id: number;
  itemType: 'link' | 'separator';
  tipo: string;
  titulo: string;
  subtitulo: string;
  icone: string;
  urlIconeCustomizado: string;
  urlImagem: string;
  url: string;
  etiqueta: string;
  cor: string;
  corSubtitulo: string;
  separatorText: string;
  separatorShowLine: boolean;
  ordem: number;
  ativo: boolean;
}

interface OfertaBioPagina {
  id: number;
  exibirNaBio: boolean;
  titulo: string;
  descricao: string;
  urlImagem: string;
  precoOriginal: number;
  precoPromocional: number;
  textoBadge: string;
  urlAcao: string;
  ordem: number;
  ativo: boolean;
}

interface BioPaginaPublica {
  id: number;
  slug: string;
  titulo: string;
  subtitulo: string;
  urlLogo: string;
  corPrimaria: string;
  corSecundaria: string;
  corFundo: string;
  corTexto: string;
  corFundoCard: string;
  corBotaoCta: string;
  borderRadius: number;
  fonte: string;
  textoBadgePromocional: string;
  exibirOfertas: boolean;
  modoExibicaoOfertas: 'slide' | 'lista';
  links: LinkBioPagina[];
  ofertas: OfertaBioPagina[];
  empresa: any;
}

@Component({
  selector: 'app-bio-pagina',
  templateUrl: './bio-pagina.component.html',
  styleUrls: ['./bio-pagina.component.scss']
})
export class BioPaginaComponent implements OnInit, OnDestroy {

  private readonly FONTES_PERMITIDAS = [
    'Inter',
    'Poppins',
    'Nunito',
    'Lato',
    'Montserrat',
    'Roboto',
    'Open Sans',
    'Raleway',
    'Merriweather',
    'Playfair Display',
    'Bebas Neue',
    'Syne',
    'Urbanist',
    'Questrial',
    'Geist'
  ];
  private readonly PROTOCOLOS_BLOQUEADOS_REGEX = /^(javascript|data|vbscript|file|blob):/i;
  private readonly PROTOCOLOS_EXTERNOS_PERMITIDOS = new Set(['http:', 'https:', 'tel:', 'mailto:']);

  bioPagina: BioPaginaPublica = null;
  carregando = true;
  erro = false;
  slug = '';

  // Carousel
  ofertaAtual = 0;
  totalOfertas = 0;
  touchStartX = 0;

  timerAutoPlay: any = null;

  // Horarios expandido
  horariosExpandido = false;

  // Responsive
  isMobile = true;

  // Heatmap
  modoHeatmap = false;
  dadosHeatmap: Map<string, {quantidade: number, percentual: number}> = new Map();
  maxQuantidadeHeatmap = 1;
  indicadoresHeatmap: IndicadorHeatmap[] = [];
  private linkFonteGoogle: HTMLLinkElement = null;

  @HostBinding('style.backgroundColor')
  get hostBackground(): string {
    return this.bioPagina?.corFundo || '#FFF8F0';
  }

  constructor(
    private route: ActivatedRoute,
    private http: HttpClient,
    private meta: Meta,
    private servicoTitulo: Title,
    private servicoRastreamentoBio: ServicoRastreamentoBio,
    private elRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    this.modoHeatmap = this.route.snapshot.queryParamMap.get('heatmap') === 'true';
    this.isMobile = window.innerWidth <= 768;
    this.carregarBio();
    this.iniciarPostHog();
  }

  ngOnDestroy() {
    this.pararAutoPlay();
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth <= 768;
    this.calcularPosicoes();
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (!event.data) return;

    // Heatmap data from admin
    if (event.data.tipo === 'bio-heatmap-data' && this.modoHeatmap) {
      this.dadosHeatmap.clear();
      this.maxQuantidadeHeatmap = event.data.maxQuantidade || 1;
      const cliques: any[] = event.data.cliques || [];
      cliques.forEach(c => {
        const chave = c.tipoItem + '_' + c.idItem;
        this.dadosHeatmap.set(chave, { quantidade: c.quantidade, percentual: c.percentual });
      });
      setTimeout(() => this.calcularPosicoes());
      return;
    }

    // Preview update (existing)
    if (event.data.tipo !== 'bio-preview-update') return;
    if (!this.bioPagina) return;
    const dados = event.data;
    if (dados.corPrimaria != null) this.bioPagina.corPrimaria = dados.corPrimaria;
    if (dados.corSecundaria != null) this.bioPagina.corSecundaria = dados.corSecundaria;
    if (dados.corFundo != null) this.bioPagina.corFundo = dados.corFundo;
    if (dados.corTexto != null) this.bioPagina.corTexto = dados.corTexto;
    if (dados.corFundoCard != null) this.bioPagina.corFundoCard = dados.corFundoCard;
    if (dados.corBotaoCta != null) this.bioPagina.corBotaoCta = dados.corBotaoCta;
    if (dados.borderRadius != null) this.bioPagina.borderRadius = dados.borderRadius;
    if (dados.fonte != null) {
      this.bioPagina.fonte = this.normalizarFontePermitida(dados.fonte);
      this.carregarFonteGoogle();
    }
  }

  carregarBio() {
    this.carregando = true;
    this.http.get('/api/bio/' + this.slug).toPromise().then((response: any) => {
      if (response && response.sucesso) {
        this.bioPagina = this.mapearBioPagina(response.data);
        this.totalOfertas = this.ofertasAtivas.length;
        this.iniciarAutoPlay();
        this.carregarFonteGoogle();
        this.configurarMetaTags();
        this.registrarVisualizacao();
        this.identificarPostHog();
      } else {
        this.erro = true;
      }
      this.carregando = false;
    }).catch(() => {
      this.erro = true;
      this.carregando = false;
    });
  }

  private mapearBioPagina(dados: any): BioPaginaPublica {
    return {
      id: dados.id,
      slug: dados.slug,
      titulo: dados.title,
      subtitulo: dados.subtitle,
      urlLogo: dados.logoUrl,
      corPrimaria: dados.primaryColor,
      corSecundaria: dados.secondaryColor,
      corFundo: dados.backgroundColor,
      corTexto: dados.textColor || '#333333',
      corFundoCard: dados.cardBackgroundColor || '#FFFFFF',
      corBotaoCta: dados.ctaButtonColor || null,
      borderRadius: dados.borderRadius != null ? dados.borderRadius : 12,
      fonte: this.normalizarFontePermitida(dados.font || ''),
      textoBadgePromocional: dados.promoBadgeText,
      exibirOfertas: dados.showOffers !== false,
      modoExibicaoOfertas: dados.offersDisplayMode === 'lista' ? 'lista' : 'slide',
      links: (dados.links || []).map((link: any) => ({
        id: link.id,
        itemType: link.itemType || 'link',
        tipo: link.type,
        titulo: link.title,
        subtitulo: link.subtitle,
        icone: link.icon,
        urlIconeCustomizado: link.imageUrl || link.customIconUrl,
        urlImagem: link.imageUrl || link.customIconUrl,
        url: link.url,
        etiqueta: link.badge || '',
        cor: link.color || null,
        corSubtitulo: link.subtitleColor || null,
        separatorText: link.separatorText || '',
        separatorShowLine: link.separatorShowLine !== false,
        ordem: link.sortOrder,
        ativo: link.isActive
      })),
      ofertas: (dados.offers || []).map((oferta: any) => ({
        id: oferta.id,
        exibirNaBio: oferta.showInBio !== false,
        titulo: oferta.title,
        descricao: oferta.description,
        urlImagem: oferta.imageUrl,
        precoOriginal: oferta.oldPrice,
        precoPromocional: oferta.newPrice,
        textoBadge: oferta.badgeText,
        urlAcao: oferta.actionUrl,
        ordem: oferta.sortOrder,
        ativo: oferta.isActive
      })),
      empresa: dados.empresa
    };
  }

  private carregarFonteGoogle() {
    const fontePermitida = this.normalizarFontePermitida(this.bioPagina?.fonte);
    const linkId = 'bio-google-font';

    if (!fontePermitida) {
      const linkExistente = document.getElementById(linkId);
      if (linkExistente?.parentNode) {
        linkExistente.parentNode.removeChild(linkExistente);
      }
      this.linkFonteGoogle = null;
      return;
    }

    if (!this.linkFonteGoogle) {
      this.linkFonteGoogle = document.getElementById(linkId) as HTMLLinkElement;
    }

    if (!this.linkFonteGoogle) {
      this.linkFonteGoogle = document.createElement('link');
      this.linkFonteGoogle.id = linkId;
      this.linkFonteGoogle.rel = 'stylesheet';
      document.head.appendChild(this.linkFonteGoogle);
    }

    const fonteEncoded = fontePermitida.replace(/ /g, '+');
    this.linkFonteGoogle.href = `https://fonts.googleapis.com/css2?family=${fonteEncoded}:wght@400;500;600;700;800&display=swap`;
  }

  private configurarMetaTags() {
    if (!this.bioPagina) return;
    const urlCanonica = this.obtenhaUrlCanonica();
    const descricao = this.bioPagina.subtitulo || this.bioPagina.titulo || 'MeuCardápio Bio';
    const imagemAbsoluta = this.obtenhaImagemOgAbsoluta();

    this.servicoTitulo.setTitle(this.bioPagina.titulo + ' | MeuCardápio Bio');
    this.meta.updateTag({ name: 'description', content: descricao });

    this.meta.updateTag({ property: 'og:title', content: this.bioPagina.titulo });
    this.meta.updateTag({ property: 'og:description', content: descricao });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: urlCanonica });
    this.meta.updateTag({ property: 'og:image', content: imagemAbsoluta });
    this.meta.updateTag({ property: 'og:image:secure_url', content: imagemAbsoluta });
    this.meta.updateTag({ property: 'og:site_name', content: 'MeuCardápio Bio' });

    this.meta.updateTag({ name: 'twitter:card', content: imagemAbsoluta ? 'summary_large_image' : 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: this.bioPagina.titulo });
    this.meta.updateTag({ name: 'twitter:description', content: descricao });
    this.meta.updateTag({ name: 'twitter:image', content: imagemAbsoluta });

    this.atualizarTagCanonical(urlCanonica);
    this.meta.updateTag({ name: 'theme-color', content: this.bioPagina.corFundo || '#FFF8F0' });
  }

  private iniciarPostHog() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true' || this.modoHeatmap) return;
    const w = window as any;
    if (w.posthog && w.posthog.__SV) return;
    const snippet = document.createElement('script');
    snippet.textContent = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host.replace(".i.posthog.com","-assets.i.posthog.com")+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group identify setPersonProperties setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags resetGroups onFeatureFlags addFeatureFlagsHandler onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey getNextSurveyStep".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('phc_Su5yrfxmlIaqLVl3NorVyCMll20alJAY165n7uPEBvE',{api_host:'https://us.i.posthog.com',defaults:'2026-01-30'})`;
    document.head.appendChild(snippet);
  }

  private identificarPostHog() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true' || this.modoHeatmap) return;
    const w = window as any;
    if (!w.posthog || !this.bioPagina) return;
    w.posthog.group('empresa', String(this.bioPagina.id), {
      nome: this.bioPagina.titulo,
      slug: this.bioPagina.slug
    });
  }

  private registrarVisualizacao() {
    if (this.route.snapshot.queryParamMap.get('preview') === 'true') return;
    this.servicoRastreamentoBio.registrarVisualizacaoPagina(this.slug);
  }

  registrarClique(tipo: string, itemId: number, url: string, titulo?: string, posicao?: number) {
    const urlSegura = this.normalizarUrlNavegavel(url);
    if (this.route.snapshot.queryParamMap.get('preview') === 'true') {
      if (urlSegura) {
        tipo === 'oferta' ? window.location.href = urlSegura : window.open(urlSegura, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    this.servicoRastreamentoBio.registrarClique(this.slug, tipo, itemId, titulo, posicao);

    if (urlSegura) {
      tipo === 'oferta' ? window.location.href = urlSegura : window.open(urlSegura, '_blank', 'noopener,noreferrer');
    }
  }

  onClickLink(event: MouseEvent, item: LinkBioPagina) {
    const href = this.obtenhaHrefDoLink(item);
    if (!href) {
      event.preventDefault();
      return;
    }

    if (this.route.snapshot.queryParamMap.get('preview') === 'true') {
      return;
    }

    this.servicoRastreamentoBio.registrarClique(
      this.slug,
      'link',
      item.id,
      item.titulo,
      this.obtenhaPosicaoDoLink(item)
    );
  }

  private ordenePorOrdem<T extends {ordem: number}>(itens: T[] | null | undefined): T[] {
    return [...(itens || [])].sort((a, b) => {
      const ordemA = a?.ordem != null ? Number(a.ordem) : Number.MAX_SAFE_INTEGER;
      const ordemB = b?.ordem != null ? Number(b.ordem) : Number.MAX_SAFE_INTEGER;
      return ordemA - ordemB;
    });
  }

  // Ofertas
  get ofertasAtivas(): any[] {
    if (!this.bioPagina?.ofertas) return [];
    return this.ordenePorOrdem(
      this.bioPagina.ofertas.filter((oferta: OfertaBioPagina) => oferta.ativo && oferta.exibirNaBio !== false)
    );
  }

  get exibeOfertasEmLista(): boolean {
    return this.bioPagina?.modoExibicaoOfertas === 'lista';
  }

  // Itens da bio
  get itensBioAtivos(): LinkBioPagina[] {
    if (!this.bioPagina?.links) return [];
    return this.ordenePorOrdem(
      this.bioPagina.links.filter((link: LinkBioPagina) => link.ativo)
    );
  }

  get linkCta(): LinkBioPagina | null {
    return this.itensBioAtivos.find((item: LinkBioPagina) => !this.isSeparator(item)) || null;
  }

  isSeparator(item: LinkBioPagina): boolean {
    return (item?.itemType || 'link') === 'separator';
  }

  obtenhaHrefDoLink(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return '';
    }
    return this.normalizarUrlNavegavel(item.url);
  }

  abreLinkEmNovaAba(item: LinkBioPagina | null): boolean {
    const href = this.obtenhaHrefDoLink(item);
    if (!href) return false;
    return !href.startsWith('tel:') && !href.startsWith('mailto:');
  }

  obtenhaRelLink(item: LinkBioPagina | null): string {
    return this.abreLinkEmNovaAba(item) ? 'noopener noreferrer nofollow' : null;
  }

  obtenhaAriaLabelLink(item: LinkBioPagina | null): string {
    if (!item) return 'Abrir link';
    const titulo = (item.titulo || 'Abrir link').trim();
    const subtitulo = (item.subtitulo || '').trim();
    return subtitulo ? `${titulo}. ${subtitulo}` : titulo;
  }

  ehLinkCta(item: LinkBioPagina): boolean {
    return !this.isSeparator(item) && !!this.linkCta && this.linkCta.id === item.id;
  }

  temThumbNoLink(item: LinkBioPagina | null): boolean {
    return Boolean(this.obtenhaUrlThumb(item) && !this.obtenhaImagemDoIcone(item));
  }

  obtenhaUrlThumb(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return '';
    }

    return this.normalizarUrlDeImagemDoLink(item.urlImagem || item.urlIconeCustomizado || '');
  }

  obtenhaPosicaoDoLink(item: LinkBioPagina): number {
    let posicao = 0;

    for (const itemAtual of this.itensBioAtivos) {
      if (this.isSeparator(itemAtual)) {
        continue;
      }

      if (itemAtual.id === item.id) {
        return posicao;
      }

      posicao++;
    }

    return 0;
  }

  // Status da loja
  get statusLoja(): { aberto: boolean; texto: string } {
    if (!this.bioPagina?.empresa) return { aberto: false, texto: 'Fechado' };
    const aberto = this.bioPagina.empresa.estaRecebendoPedidos;
    return {
      aberto: aberto,
      texto: aberto ? 'Aberto' : 'Fechado'
    };
  }

  // Carousel controls
  proximaOferta() {
    if (this.ofertaAtual < this.totalOfertas - 1) {
      this.ofertaAtual++;
    }
  }

  ofertaAnterior() {
    if (this.ofertaAtual > 0) {
      this.ofertaAtual--;
    }
  }

  irParaOferta(index: number) {
    this.ofertaAtual = index;
    this.reiniciarAutoPlay();
    setTimeout(() => this.calcularPosicoes(), 350);
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent) {
    const touchEndX = event.changedTouches[0].clientX;
    const diff = this.touchStartX - touchEndX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        this.proximaOferta();
      } else {
        this.ofertaAnterior();
      }
      this.reiniciarAutoPlay();
    }
  }

  iniciarAutoPlay() {
    this.pararAutoPlay();
    if (this.totalOfertas <= 1 || this.exibeOfertasEmLista) return;
    this.timerAutoPlay = setInterval(() => {
      this.ofertaAtual = (this.ofertaAtual + 1) % this.totalOfertas;
      setTimeout(() => this.calcularPosicoes(), 350);
    }, 5000);
  }

  reiniciarAutoPlay() {
    if (this.timerAutoPlay && !this.exibeOfertasEmLista) {
      this.pararAutoPlay();
      setTimeout(() => this.iniciarAutoPlay());
    }
  }

  pararAutoPlay() {
    if (this.timerAutoPlay) {
      clearInterval(this.timerAutoPlay);
      this.timerAutoPlay = null;
    }
  }

  toggleHorarios() {
    this.horariosExpandido = !this.horariosExpandido;
  }

  // Icone helpers
  obtenhaIconeDoTipo(tipo: string): string {
    const icones: any = {
      'cardapio': 'fas fa-utensils',
      'whatsapp': 'fab fa-whatsapp',
      'instagram': 'fab fa-instagram',
      'ifood': 'fas fa-motorcycle',
      '99food': 'fas fa-motorcycle',
      'aiqfome': 'fas fa-hamburger',
      'telefone': 'fas fa-phone-alt',
      'localizacao': 'fas fa-map-marker-alt',
      'reserva': 'fas fa-calendar-check',
      'site': 'fas fa-globe'
    };
    return icones[tipo] || 'fas fa-link';
  }

  obtenhaIconeDoLink(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return 'fas fa-link';
    }

    const icone = String(item.icone || '').trim();
    if (icone && !this.ehIconeImagem(icone)) {
      return icone;
    }

    return this.obtenhaIconeDoTipo(item.tipo);
  }

  ehIconeImagem(icone: string | null | undefined): boolean {
    const valor = String(icone || '').trim();
    if (!valor) {
      return false;
    }

    return /^assets\//i.test(valor) || /\.(svg|png|jpe?g|webp|gif)$/i.test(valor);
  }

  obtenhaImagemDoIcone(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return '';
    }

    const icone = String(item.icone || '').trim();
    if (this.ehIconeImagem(icone)) {
      return this.normalizarUrlDeImagemDoLink(icone);
    }

    const imagemFallback = item.urlImagem || item.urlIconeCustomizado || '';
    if (imagemFallback) {
      return this.normalizarUrlDeImagemDoLink(imagemFallback);
    }

    return this.obtenhaLogoDoTipo(item.tipo);
  }

  obtenhaCorDecorativaDoLink(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return '#5D7088';
    }

    const icone = String(item.icone || '').trim();
    if (icone && !this.ehIconeImagem(icone)) {
      return this.obtenhaTemaDoIcone(icone).cor;
    }

    return this.obtenhaCorDoTipo(item.tipo) || '#5D7088';
  }

  obtenhaFundoDecorativoDoLink(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return '#F2F5F8';
    }

    const icone = String(item.icone || '').trim();
    if (icone && !this.ehIconeImagem(icone)) {
      return this.obtenhaTemaDoIcone(icone).fundo;
    }

    const cor = this.obtenhaCorDoTipo(item.tipo);
    return cor ? `${cor}15` : '#F2F5F8';
  }

  obtenhaCorDoTipo(tipo: string): string {
    const cores: any = {
      'whatsapp': '#25D366',
      'instagram': '#E1306C',
      'ifood': '#EA1D2C',
      '99food': '#FFCC00',
      'aiqfome': '#7D0DC3',
      'telefone': '#1976D2',
      'localizacao': '#F57C00',
      'reserva': '#7B1FA2',
      'cardapio': '#D97745',
      'site': '#4A7A96'
    };
    return cores[tipo] || null;
  }

  obtenhaCorDoLink(item: LinkBioPagina | null): string {
    if (!item || this.isSeparator(item)) {
      return null;
    }

    return item.cor || this.obtenhaCorDoTipo(item.tipo);
  }

  obtenhaCorDoSubtituloDoLink(item: LinkBioPagina | null): string {
    return item?.corSubtitulo || null;
  }

  obtenhaLogoDoTipo(tipo: string): string {
    const logos: any = {
      'whatsapp': 'assets/icons/whatsapp_logo_oficial.png',
      'ifood': 'assets/icons/logo-ifood.png',
      '99food': 'assets/icons/logo-99food.png',
      'aiqfome': 'assets/icons/logo-aiqfome.png',
      'instagram': 'assets/fidelidade/icones/instagram.svg',
    };
    return logos[tipo] || '';
  }

  private obtenhaTemaDoIcone(icone: string | null | undefined): { cor: string; fundo: string } {
    const valor = String(icone || '').trim();
    const temas: { [key: string]: { cor: string; fundo: string } } = {
      'fas fa-link': { cor: '#4E72B8', fundo: '#EEF3FB' },
      'fas fa-store': { cor: '#6A63D9', fundo: '#F1EFFE' },
      'fas fa-utensils': { cor: '#D97745', fundo: '#FCEFE7' },
      'fas fa-hamburger': { cor: '#D38A2C', fundo: '#FDF1DF' },
      'fas fa-motorcycle': { cor: '#2F8F83', fundo: '#E8F6F3' },
      'fas fa-phone-alt': { cor: '#2F7DE1', fundo: '#EAF2FE' },
      'fas fa-map-marker-alt': { cor: '#2E9E74', fundo: '#EAF8F1' },
      'fas fa-calendar-check': { cor: '#8A63D2', fundo: '#F3EEFC' },
      'fas fa-shopping-bag': { cor: '#C45B74', fundo: '#FCECF1' },
      'fas fa-ticket-alt': { cor: '#E28A2E', fundo: '#FEF2E5' },
      'fas fa-star': { cor: '#C89B2D', fundo: '#FCF4E3' },
      'fas fa-globe': { cor: '#4A7A96', fundo: '#ECF4F8' },
      'fab fa-instagram': { cor: '#E1306C', fundo: '#FDEEF3' }
    };

    return temas[valor] || { cor: '#5D7088', fundo: '#F2F5F8' };
  }

  obtenhaFamiliaFonte(): string | null {
    const fontePermitida = this.normalizarFontePermitida(this.bioPagina?.fonte);
    if (!fontePermitida) {
      return null;
    }

    const fallback = this.ehFonteSerif(fontePermitida)
      ? 'Georgia, Cambria, "Times New Roman", serif'
      : 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

    return `"${fontePermitida}", ${fallback}`;
  }

  obtenhaHeatmap(tipo: string, id: number): {quantidade: number, percentual: number} {
    const chave = tipo + '_' + id;
    return this.dadosHeatmap.get(chave) || { quantidade: 0, percentual: 0 };
  }

  obtenhaCorHeatmap(tipo: string, id: number): string {
    const dados = this.obtenhaHeatmap(tipo, id);
    if (dados.quantidade === 0) return 'transparent';
    const intensidade = (dados.quantidade / this.maxQuantidadeHeatmap) * 100;

    if (intensidade <= 20) return 'rgba(255, 193, 7, 0.15)';
    if (intensidade <= 50) return 'rgba(255, 152, 0, 0.25)';
    if (intensidade <= 80) return 'rgba(255, 120, 0, 0.35)';
    return 'rgba(244, 67, 54, 0.40)';
  }

  calcularPosicoes() {
    if (!this.modoHeatmap || this.dadosHeatmap.size === 0) {
      this.indicadoresHeatmap = [];
      return;
    }
    const container = this.elRef.nativeElement.querySelector('.bio-page');
    if (!container) return;
    const containerRect = container.getBoundingClientRect();
    const elementos = container.querySelectorAll('[data-heatmap]');
    const indicadores: IndicadorHeatmap[] = [];

    elementos.forEach((el: Element) => {
      const chave = el.getAttribute('data-heatmap');
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      const [tipo, idStr] = chave.split('_');
      const id = parseInt(idStr, 10);
      const dados = this.obtenhaHeatmap(tipo, id);
      const computed = getComputedStyle(el);
      const br = parseFloat(computed.borderRadius) || 0;
      indicadores.push({
        top: rect.top - containerRect.top + container.scrollTop,
        left: rect.left - containerRect.left,
        width: rect.width,
        height: rect.height,
        cor: this.obtenhaCorHeatmap(tipo, id),
        quantidade: dados.quantidade,
        percentual: dados.percentual,
        borderRadius: br,
        eTopClique: false
      });
    });

    // Identificar o top clique
    const maxQuantidade = Math.max(...indicadores.map(i => i.quantidade));
    if (maxQuantidade > 0) {
      indicadores.forEach(i => {
        i.eTopClique = i.quantidade === maxQuantidade;
      });
    }

    this.indicadoresHeatmap = indicadores;

    this.cdr.detectChanges();
  }

  private normalizarFontePermitida(fonte: string): string {
    const valor = String(fonte || '').trim();
    if (!valor) return '';
    return this.FONTES_PERMITIDAS.includes(valor) ? valor : '';
  }

  private ehFonteSerif(fonte: string): boolean {
    return fonte === 'Merriweather' || fonte === 'Playfair Display';
  }

  private normalizarUrlNavegavel(url: string): string {
    const valor = String(url || '').trim();
    if (!valor) {
      return '';
    }

    if (this.PROTOCOLOS_BLOQUEADOS_REGEX.test(valor)) {
      return '';
    }

    if (valor.startsWith('/') || valor.startsWith('./') || valor.startsWith('../')) {
      return valor;
    }

    if (/^tel:/i.test(valor)) {
      const numeroLimpo = valor.replace(/^tel:/i, '').replace(/[^\d+]/g, '');
      return numeroLimpo ? `tel:${numeroLimpo}` : '';
    }

    try {
      const valorComProtocolo = /^[a-z][a-z0-9+.-]*:/i.test(valor) ? valor : `https://${valor}`;
      const parsed = new URL(valorComProtocolo);

      if (!this.PROTOCOLOS_EXTERNOS_PERMITIDOS.has(parsed.protocol)) {
        return '';
      }

      return parsed.toString();
    } catch {
      return '';
    }
  }

  private normalizarUrlDeImagemDoLink(url: string | null | undefined): string {
    const valor = String(url || '').trim();
    if (!valor) {
      return '';
    }

    if (/^(https?:)?\/\//i.test(valor) || /^(data|blob):/i.test(valor)) {
      return valor;
    }

    if (valor.startsWith('/')) {
      return valor;
    }

    if (/^assets\//i.test(valor)) {
      return `/${valor.replace(/^\/+/, '')}`;
    }

    if (valor.startsWith('./') || valor.startsWith('../')) {
      try {
        return new URL(valor, `${window.location.origin}/`).toString();
      } catch {
        return valor;
      }
    }

    return '/images/empresa/' + valor.replace(/^\/+/, '');
  }

  private obtenhaImagemOgAbsoluta(): string {
    const logo = String(this.bioPagina?.urlLogo || '').trim();
    if (!logo) {
      return `${window.location.origin}/assets/icons/icon-192x192.png`;
    }

    if (/^https?:\/\//i.test(logo)) {
      return logo;
    }

    return `${window.location.origin}/images/empresa/${logo.replace(/^\/+/, '')}`;
  }

  private obtenhaUrlCanonica(): string {
    return `${window.location.origin}/bio/${this.slug}`;
  }

  private atualizarTagCanonical(url: string): void {
    if (!url) return;

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', url);
  }
}
